import { Issuers } from '@/constants/issuers';

import { type TokenBase, isRwaIssuer } from '@/utils/0x/token-registry.types';

export type GeoRestrictedToken = Readonly<
  Pick<TokenBase, 'issuer' | 'rwaPolicy' | 'rwaClassification'>
>;

export type TokenRestriction =
  | Readonly<{ kind: 'none' }>
  | Readonly<{ kind: 'everywhere' }>
  | Readonly<{ kind: 'countries'; countries: readonly string[] }>;

export type TradeGeoStatus = 'geo-checking' | 'geo-allowed' | 'geo-blocked';

type GeoRestrictionRule = Readonly<{
  match: Readonly<{ issuer: Issuers; rwaIssuer?: true } | { platform: string }>;
  countries: readonly string[];
}>;

const NON_US_COUNTRIES: readonly string[] = ['US'];

const SANCTIONED_COUNTRIES: readonly string[] = [
  'AF',
  'BY',
  'CA',
  'CU',
  'KP',
  'IR',
  'LY',
  'MM',
  'RU',
  'SO',
  'SS',
  'SD',
  'SY',
  'UA',
];

/**
 * Additive token geo-restrictions layered on top of the Token API policy. A
 * token is restricted in the union of countries from every rule matching its
 * issuer or RWA platform.
 */
const GEO_RESTRICTION_RULES = [
  { match: { issuer: Issuers.MAPLE }, countries: NON_US_COUNTRIES },
  { match: { platform: 'Coinbase Global' }, countries: ['AU', 'SG', 'GB', 'CA'] },
  { match: { issuer: Issuers.COINBASE, rwaIssuer: true }, countries: ['AU', 'SG', 'GB', 'CA'] },
  { match: { issuer: Issuers.ONDO }, countries: SANCTIONED_COUNTRIES },
  { match: { issuer: Issuers.BSTOCKS }, countries: SANCTIONED_COUNTRIES },
  { match: { issuer: Issuers.CENTRIFUGE }, countries: SANCTIONED_COUNTRIES },
] as const satisfies readonly GeoRestrictionRule[];

/** Countries barred by each policy need Matcha recognises; any other need is never met. */
const POLICY_NEED_COUNTRIES: ReadonlyMap<string, readonly string[]> = new Map([
  ['optIn', []],
  ['nonUS', NON_US_COUNTRIES],
]);

/** Without a policy row, an issuer flagged RWA gets the `nonUS` restriction. */
const LEGACY_RWA_COUNTRIES = NON_US_COUNTRIES;

const UNRESTRICTED: TokenRestriction = { kind: 'none' };
const RESTRICTED_EVERYWHERE: TokenRestriction = { kind: 'everywhere' };

const restrictedIn = (countries: readonly string[]): TokenRestriction =>
  countries.length === 0 ? UNRESTRICTED : { kind: 'countries', countries: [...new Set(countries)] };

const policyRestriction = ({ issuer, rwaPolicy }: GeoRestrictedToken): TokenRestriction => {
  if (rwaPolicy == null) return restrictedIn(isRwaIssuer(issuer) ? LEGACY_RWA_COUNTRIES : []);
  if (rwaPolicy.blocked != null) return RESTRICTED_EVERYWHERE;

  const countries: string[] = [];
  for (const { need } of rwaPolicy.requires ?? []) {
    const needCountries = POLICY_NEED_COUNTRIES.get(need);
    if (!needCountries) return RESTRICTED_EVERYWHERE;
    countries.push(...needCountries);
  }
  return restrictedIn(countries);
};

const ruleMatchesToken = ({ match }: GeoRestrictionRule, token: GeoRestrictedToken): boolean =>
  'issuer' in match
    ? match.issuer === token.issuer?.name?.toLowerCase() &&
      (!match.rwaIssuer || isRwaIssuer(token.issuer))
    : match.platform === token.rwaClassification?.platformName;

/**
 * Where this token cannot be traded. The Token API `rwaPolicy` is authoritative
 * when present; without one, an issuer flagged RWA is barred from the US. Issuer
 * and platform rules add countries on top of either.
 */
export const getTokenRestriction = (token?: GeoRestrictedToken): TokenRestriction => {
  if (!token) return UNRESTRICTED;

  const policy = policyRestriction(token);
  if (policy.kind === 'everywhere') return policy;

  return restrictedIn([
    ...(policy.kind === 'countries' ? policy.countries : []),
    ...GEO_RESTRICTION_RULES.filter((rule) => ruleMatchesToken(rule, token)).flatMap(
      ({ countries }) => countries,
    ),
  ]);
};

/** True when the geo header was absent, so no country could be resolved. */
export const isUnknownCountry = (country: string): boolean => !country || country === 'unknown';

const restrictionAppliesIn = (restriction: TokenRestriction, country: string): boolean => {
  switch (restriction.kind) {
    case 'none':
      return false;
    case 'everywhere':
      return true;
    case 'countries':
      return isUnknownCountry(country) || restriction.countries.includes(country);
  }
};

/** Fails closed for country-restricted tokens when the country cannot be resolved. */
export const isTokenRestrictedInCountry = (
  token: GeoRestrictedToken | undefined,
  country: string,
): boolean => restrictionAppliesIn(getTokenRestriction(token), country);

/**
 * Whether the tokens can be traded together. `country` is undefined while the
 * geo check is pending: a token restricted everywhere blocks at once, and only
 * country-restricted tokens wait for the country.
 */
export const getTradeGeoStatus = (
  tokens: readonly (GeoRestrictedToken | undefined)[],
  country: string | undefined,
): TradeGeoStatus => {
  const restrictions = tokens.map(getTokenRestriction);
  if (restrictions.some(({ kind }) => kind === 'everywhere')) return 'geo-blocked';
  if (!restrictions.some(({ kind }) => kind === 'countries')) return 'geo-allowed';
  if (country === undefined) return 'geo-checking';
  return restrictions.some((restriction) => restrictionAppliesIn(restriction, country))
    ? 'geo-blocked'
    : 'geo-allowed';
};

/** {@link getTradeGeoStatus} for one token; a pending check is not a block. */
export const isTokenGeoBlocked = (
  token: GeoRestrictedToken | undefined,
  country: string | undefined,
): boolean => getTradeGeoStatus([token], country) === 'geo-blocked';

/**
 * Whether `country` bars an otherwise unspecified RWA token: the legacy
 * `nonUS` restriction. Used by the backwards-compatible geo-check response,
 * where no token is available. Fails closed for an unresolved country.
 */
export const isRwaRestrictedInCountry = (country: string): boolean =>
  isUnknownCountry(country) || LEGACY_RWA_COUNTRIES.includes(country);
